/**
 * Thunderbird-style compose font family list:
 * 1) Variable Width / Fixed Width
 * 2) Helvetica, Arial · Times · Courier
 * 3) Alphabetized available system fonts
 */

export interface FontFamilyOption {
  value: string;
  label: string;
}

export type FontFamilySection =
  | { kind: 'options'; options: FontFamilyOption[] }
  | { kind: 'separator' };

/** Semantic + common stacks (Thunderbird top sections). */
export const THUNDERBIRD_PRIMARY_FONTS: FontFamilyOption[] = [
  { value: '', label: 'Variable Width' },
  { value: 'monospace', label: 'Fixed Width' },
];

export const THUNDERBIRD_COMMON_FONTS: FontFamilyOption[] = [
  { value: 'Helvetica, Arial, sans-serif', label: 'Helvetica, Arial' },
  { value: 'Times, "Times New Roman", serif', label: 'Times' },
  { value: 'Courier, "Courier New", monospace', label: 'Courier' },
];

/**
 * Candidate system fonts commonly shown in Thunderbird on Windows.
 * Filtered at runtime to fonts actually available in the browser.
 */
export const THUNDERBIRD_SYSTEM_FONT_CANDIDATES: string[] = [
  'Agency FB',
  'Algerian',
  'Arial',
  'Arial Black',
  'Arial Narrow',
  'Arial Rounded MT Bold',
  'Bahnschrift',
  'Baskerville Old Face',
  'Bauhaus 93',
  'Bell MT',
  'Berlin Sans FB',
  'Bernard MT Condensed',
  'Blackadder ITC',
  'Bodoni MT',
  'Book Antiqua',
  'Bookman Old Style',
  'Bradley Hand ITC',
  'Britannic Bold',
  'Broadway',
  'Brush Script MT',
  'Calibri',
  'Calibri Light',
  'Californian FB',
  'Calisto MT',
  'Cambria',
  'Cambria Math',
  'Candara',
  'Castellar',
  'Centaur',
  'Century',
  'Century Gothic',
  'Century Schoolbook',
  'Chiller',
  'Colonna MT',
  'Comic Sans MS',
  'Consolas',
  'Constantia',
  'Cooper Black',
  'Copperplate Gothic Bold',
  'Copperplate Gothic Light',
  'Corbel',
  'Courier New',
  'Curlz MT',
  'Edwardian Script ITC',
  'Elephant',
  'Engravers MT',
  'Eras Bold ITC',
  'Eras Demi ITC',
  'Eras Light ITC',
  'Eras Medium ITC',
  'Felix Titling',
  'Footlight MT Light',
  'Forte',
  'Franklin Gothic Book',
  'Franklin Gothic Demi',
  'Franklin Gothic Heavy',
  'Franklin Gothic Medium',
  'Freestyle Script',
  'French Script MT',
  'Gabriola',
  'Gadugi',
  'Garamond',
  'Georgia',
  'Gigi',
  'Gill Sans MT',
  'Gloucester MT Extra Condensed',
  'Goudy Old Style',
  'Goudy Stout',
  'Haettenschweiler',
  'Harlow Solid Italic',
  'Harrington',
  'High Tower Text',
  'Impact',
  'Imprint MT Shadow',
  'Informal Roman',
  'Ink Free',
  'Jokerman',
  'Juice ITC',
  'Kristen ITC',
  'Kunstler Script',
  'Leelawadee UI',
  'Lucida Bright',
  'Lucida Calligraphy',
  'Lucida Console',
  'Lucida Fax',
  'Lucida Handwriting',
  'Lucida Sans',
  'Lucida Sans Typewriter',
  'Lucida Sans Unicode',
  'Magneto',
  'Maiandra GD',
  'Matura MT Script Capitals',
  'Microsoft Himalaya',
  'Microsoft JhengHei',
  'Microsoft New Tai Lue',
  'Microsoft PhagsPa',
  'Microsoft Sans Serif',
  'Microsoft Tai Le',
  'Microsoft YaHei',
  'Microsoft Yi Baiti',
  'MingLiU-ExtB',
  'Mistral',
  'Modern No. 20',
  'Mongolian Baiti',
  'Monotype Corsiva',
  'MV Boli',
  'Myanmar Text',
  'Niagara Engraved',
  'Niagara Solid',
  'Nirmala UI',
  'OCR A Extended',
  'Old English Text MT',
  'Onyx',
  'Palace Script MT',
  'Palatino Linotype',
  'Papyrus',
  'Parchment',
  'Perpetua',
  'Perpetua Titling MT',
  'Playbill',
  'Poor Richard',
  'Pristina',
  'Rage Italic',
  'Ravie',
  'Rockwell',
  'Rockwell Condensed',
  'Rockwell Extra Bold',
  'Script MT Bold',
  'Segoe Print',
  'Segoe Script',
  'Segoe UI',
  'Segoe UI Black',
  'Segoe UI Emoji',
  'Segoe UI Historic',
  'Segoe UI Light',
  'Segoe UI Semibold',
  'Segoe UI Symbol',
  'Showcard Gothic',
  'SimSun',
  'Sitka',
  'Snap ITC',
  'Stencil',
  'Sylfaen',
  'Symbol',
  'Tahoma',
  'Tempus Sans ITC',
  'Times New Roman',
  'Trebuchet MS',
  'Tw Cen MT',
  'Verdana',
  'Viner Hand ITC',
  'Vivaldi',
  'Vladimir Script',
  'Webdings',
  'Wide Latin',
  'Wingdings',
  'Wingdings 2',
  'Wingdings 3',
  // Cross-platform / macOS common
  'Helvetica',
  'Helvetica Neue',
  'Menlo',
  'Monaco',
  'Geneva',
  'Optima',
  'Futura',
  'Gill Sans',
  'Apple Color Emoji',
];

const COMMON_LABELS = new Set(THUNDERBIRD_COMMON_FONTS.map((f) => f.label.toLowerCase()));

function quoteFontFamily(name: string): string {
  return /[\s,]/.test(name) ? `"${name.replace(/"/g, '')}"` : name;
}

/** Detect whether a named font is installed (canvas width probe). */
export function isSystemFontAvailable(fontName: string): boolean {
  if (typeof document === 'undefined') return false;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  const probe = 'mmmmmmmmmmlliWWWW@ilI1';
  const size = '72px ';
  ctx.font = `${size}monospace`;
  const baselineMono = ctx.measureText(probe).width;
  ctx.font = `${size}sans-serif`;
  const baselineSans = ctx.measureText(probe).width;
  const quoted = quoteFontFamily(fontName);
  ctx.font = `${size}${quoted}, monospace`;
  const withMono = ctx.measureText(probe).width;
  ctx.font = `${size}${quoted}, sans-serif`;
  const withSans = ctx.measureText(probe).width;
  return withMono !== baselineMono || withSans !== baselineSans;
}

function toSystemOption(fontName: string): FontFamilyOption {
  return {
    value: quoteFontFamily(fontName),
    label: fontName,
  };
}

/**
 * Build Thunderbird-ordered font sections for the compose toolbar.
 * System fonts are filtered to those available in the current browser.
 */
export function buildThunderbirdFontSections(
  candidates: string[] = THUNDERBIRD_SYSTEM_FONT_CANDIDATES,
): FontFamilySection[] {
  const available = candidates
    .filter((name) => {
      const lower = name.toLowerCase();
      // Avoid duplicating the short common-family entries
      if (lower === 'helvetica' || lower === 'times' || lower === 'courier') return false;
      if (COMMON_LABELS.has(lower)) return false;
      return isSystemFontAvailable(name);
    })
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(toSystemOption);

  const sections: FontFamilySection[] = [
    { kind: 'options', options: THUNDERBIRD_PRIMARY_FONTS },
    { kind: 'separator' },
    { kind: 'options', options: THUNDERBIRD_COMMON_FONTS },
  ];

  if (available.length > 0) {
    sections.push({ kind: 'separator' }, { kind: 'options', options: available });
  }

  return sections;
}

/** Flatten sections for lookups (value → label). */
export function flattenFontSections(sections: FontFamilySection[]): FontFamilyOption[] {
  return sections.flatMap((section) => (section.kind === 'options' ? section.options : []));
}
